import { useState, useEffect } from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { SquaresFour, Users, Package, CurrencyCircleDollar, WarningCircle, SignOut, ChartLineUp, UserCircle, FileText, MagnifyingGlass, Percent, ClockCounterClockwise, DeviceMobile, List, X, ShieldStar, Barcode, Article, Receipt, Cube, TrendUp, Bank } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { ForceTwoFASetup } from "./ForceTwoFASetup";
import { VendorWatermark } from "./VendorWatermark";
import { OfflineBanner } from "./OfflineBanner";
import { ApiSpeedIndicator } from "./ApiSpeedIndicator";
import { BottomNav } from "./BottomNav";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, testId: "nav-dashboard", full: true },
  { to: "/clientes", label: "Clientes", icon: Users, testId: "nav-clients" },
  { to: "/pedidos", label: "Pedidos", icon: Package, testId: "nav-orders" },
  { to: "/orcamentos", label: "Orçamentos", icon: FileText, testId: "nav-quotes" },
  { to: "/produtos", label: "Produtos", icon: Cube, testId: "nav-products", full: true },
  { to: "/consulta", label: "Consulta de Cliente", icon: MagnifyingGlass, testId: "nav-lookup" },
  { to: "/financeiro", label: "Financeiro", icon: CurrencyCircleDollar, testId: "nav-finance" },
  { to: "/contas-a-pagar", label: "Contas a Pagar", icon: Receipt, testId: "nav-payables", full: true },
  { to: "/fluxo-caixa", label: "Fluxo de Caixa", icon: TrendUp, testId: "nav-cashflow", full: true },
  { to: "/comissoes", label: "Comissões", icon: Percent, testId: "nav-commissions" },
  { to: "/lucro", label: "Lucro Real", icon: ChartLineUp, testId: "nav-profit", full: true },
  { to: "/devedores", label: "Devedores", icon: WarningCircle, testId: "nav-debtors", full: true },
  { to: "/dispositivos", label: "Dispositivos", icon: DeviceMobile, testId: "nav-devices", admin: true },
  { to: "/auditoria", label: "Auditoria", icon: ClockCounterClockwise, testId: "nav-audit", admin: true },
  { to: "/seguranca", label: "Segurança", icon: ShieldStar, testId: "nav-security", admin: true },
  { to: "/notas", label: "Notas Fiscais", icon: Article, testId: "nav-nfe-notes", full: true },
  { to: "/boletos", label: "Boletos", icon: Barcode, testId: "nav-boletos", full: true },
  { to: "/nfe-config", label: "Config. NF-e", icon: Barcode, testId: "nav-nfe-config", admin: true },
  { to: "/sicredi-config", label: "Config. Boletos", icon: Bank, testId: "nav-sicredi-config", admin: true },
  { to: "/perfil", label: "Perfil", icon: UserCircle, testId: "nav-profile" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-500">
        Carregando...
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_setup_2fa) return <ForceTwoFASetup />;

  const navItems = NAV.filter((n) => (!n.full || user.role !== "vendedor") && (!n.admin || user.role === "admin"));

  const NavLinks = ({ onNavigate }) => (
    <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
      {navItems.map(({ to, label, icon: Icon, testId }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          data-testid={testId}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors ${
              isActive ? "bg-indigo-600 text-white" : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-900"
            }`
          }
        >
          <Icon size={18} />
          {label}
        </NavLink>
      ))}
    </nav>
  );

  const UserFooter = () => (
    <div className="border-t border-zinc-800 p-4" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
      <p className="text-xs text-zinc-500 uppercase tracking-[0.15em] mb-1">
        {user.role === "vendedor" ? "Vendedor" : user.role === "admin" ? "Administrador" : "Usuário"}
      </p>
      <p className="text-sm text-zinc-300 truncate mb-3" data-testid="current-user-name">{user.name}</p>
      <button
        data-testid="logout-button"
        onClick={logout}
        className="flex items-center gap-2 text-sm text-zinc-400 hover:text-rose-400 transition-colors"
      >
        <SignOut size={16} /> Sair
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 flex">
      <OfflineBanner />
      <ApiSpeedIndicator />
      {user.role !== "admin" && <VendorWatermark user={user} />}

      {/* Header mobile fixo (respeita notch) */}
      <header
        data-testid="mobile-header"
        className="lg:hidden fixed top-0 inset-x-0 z-40 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 flex items-center gap-3 px-4 h-14"
        style={{ paddingTop: "env(safe-area-inset-top)", height: "calc(3.5rem + env(safe-area-inset-top))" }}
      >
        <button
          data-testid="mobile-menu-button"
          onClick={() => setMenuOpen(true)}
          aria-label="Abrir menu"
          className="p-2 -ml-2 text-zinc-300 hover:text-white transition-colors"
        >
          <List size={22} weight="bold" />
        </button>
        <ChartLineUp size={20} weight="bold" className="text-indigo-500" />
        <span className="font-display font-semibold tracking-tight">FluxoCRM</span>
      </header>

      {/* Drawer mobile */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div data-testid="mobile-menu-overlay" className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
          <aside
            data-testid="mobile-menu-drawer"
            className="absolute inset-y-0 left-0 w-[80%] max-w-[18rem] bg-zinc-950 border-r border-zinc-800 flex flex-col"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
          >
            <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <ChartLineUp size={20} weight="bold" className="text-indigo-500" />
                <span className="font-display font-semibold tracking-tight">FluxoCRM</span>
              </div>
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Fechar menu"
                className="p-2 text-zinc-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <NavLinks onNavigate={() => setMenuOpen(false)} />
            <UserFooter />
          </aside>
        </div>
      )}

      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-zinc-800 flex-col fixed inset-y-0 left-0 bg-zinc-950 z-20">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-zinc-800">
          <ChartLineUp size={22} weight="bold" className="text-indigo-500" />
          <span className="font-display font-semibold text-lg tracking-tight">FluxoCRM</span>
        </div>
        <NavLinks />
        <UserFooter />
      </aside>

      <main className="flex-1 lg:ml-60 min-w-0">
        <div
          className="p-4 sm:p-6 lg:p-8 pt-[calc(3.5rem+env(safe-area-inset-top)+1rem)] lg:pt-8 pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-8"
          style={{ paddingLeft: "max(1rem, env(safe-area-inset-left))", paddingRight: "max(1rem, env(safe-area-inset-right))" }}
        >
          <Outlet />
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
